import { enumMembers, type ApiSchema } from "../apiSchema";
import { CHECK_STATUSES, readCheckResults, type CheckResult, type CheckStatus } from "../content/reviewDecoders";
import { readArray, readBoolean, readNullableInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";

/**
 * A release's check report (knowledge-data.md, D4): every member's terms checked against
 * one model version, then the shapes and consistency checks on the merged release. Served with a dataset version, and in a
 * `dataset_checks_failed` refusal's `details.checks`.
 */

export type MemberCheckStatus = ApiSchema<"MemberCheckStatus">;

export type TermsOutcome = ApiSchema<"TermsOutcome">;
export type ShapesOutcome = ApiSchema<"ShapesOutcome">;

const MEMBER_CHECK_STATUSES = enumMembers<MemberCheckStatus>({ pass: true, fail: true, cannot_run: true, not_determined: true, no_instance_data: true });
const TERMS_OUTCOMES = enumMembers<TermsOutcome>({ pass: true, fail: true });
const SHAPES_OUTCOMES = enumMembers<ShapesOutcome>({ pass: true, fail: true, cannot_run: true, not_determined: true });

export interface MemberProblem {
  /** Counting from 0; `null` is about the whole member. */
  readonly statement: number | null;
  readonly reason: string;
}

export interface MemberCheck {
  readonly extraction: string;
  /** The model version the member was made against; may be older than the release's. */
  readonly writtenAgainst: string | null;
  readonly status: MemberCheckStatus;
  /** `null` for an annotation result. */
  readonly terms: TermsOutcome | null;
  /** `not_determined` when the engine listed only some findings and none involves this member. `null` for an annotation result. */
  readonly shapes: ShapesOutcome | null;
  /** Why it did not pass; listed for the first failing members only, so may be empty. */
  readonly problems: readonly MemberProblem[];
}

export interface ReleaseChecks {
  /** The one model version every member was checked against: the project's newest when the checks started (a snapshot). */
  readonly contentVersion: string;
  readonly status: CheckStatus;
  /** The version's shapes, run once on the merged release as published; each violation also names its members. */
  readonly shapes: readonly CheckResult[];
  readonly members: readonly MemberCheck[];
  /** The consistency checks on the merged release, each with the rules it ran. */
  readonly consistency: readonly CheckResult[];
  readonly engine: string;
  /** Every finding of every failed shapes check is listed, so each member's shapes outcome is established. */
  readonly attributionComplete: boolean;
  /** The report kept to its byte budget: some text shortened, or findings or reasons left out (statuses and counts are complete). */
  readonly shortened: boolean;
}

export function readReleaseChecks(value: unknown): ReleaseChecks {
  const checks = readObject(value, "release checks");
  const consistency = readObject(checks["consistency"], "consistency report");
  return {
    contentVersion: readString(checks, "content_version"),
    status: readOneOf(checks, "status", CHECK_STATUSES),
    shapes: readCheckResults(readObject(checks["shapes"], "shapes report")["results"]),
    members: readArray(checks["members"], "member checks", readMemberCheck),
    consistency: readCheckResults(consistency["results"]),
    engine: readString(consistency, "engine"),
    attributionComplete: readBoolean(checks, "attribution_complete"),
    shortened: readBoolean(checks, "shortened"),
  };
}

function readMemberCheck(value: unknown): MemberCheck {
  const member = readObject(value, "member check");
  return {
    extraction: readString(member, "extraction"),
    writtenAgainst: readNullableString(member, "written_against"),
    status: readOneOf(member, "status", MEMBER_CHECK_STATUSES),
    terms: member["terms"] == null ? null : readOneOf(member, "terms", TERMS_OUTCOMES),
    shapes: member["shapes"] == null ? null : readOneOf(member, "shapes", SHAPES_OUTCOMES),
    problems: readArray(member["problems"], "member problems", (item) => {
      const problem = readObject(item, "member problem");
      return { statement: readNullableInteger(problem, "statement"), reason: readString(problem, "reason") };
    }),
  };
}
